import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MultiSelect } from '../components/FilterBar';

const VCH_META = {
  'Purchase':      { color:'var(--blue)',   bg:'var(--blue-bg)',   border:'var(--blue-bd)'   },
  'Sale':          { color:'var(--green)',  bg:'var(--green-bg)',  border:'var(--green-bd)'  },
  'Sale Return':   { color:'var(--amber)',  bg:'var(--amber-bg)',  border:'var(--amber-bd)'  },
  'Purch Return':  { color:'var(--purple)', bg:'var(--purple-bg)', border:'var(--purple-bd)' },
  'Transfer':      { color:'var(--blue)',   bg:'var(--blue-bg)',   border:'var(--blue-bd)'   },
  'Stock Journal': { color:'var(--amber)',  bg:'var(--amber-bg)',  border:'var(--amber-bd)'  },
};

const API   = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('bc_token') || '';

const PFX = 'sm_';
function getP(key) { return new URLSearchParams(window.location.search).get(PFX + key) || ''; }
function setP(key, val) {
  const sp = new URLSearchParams(window.location.search);
  const v  = Array.isArray(val) ? val.join('|') : (val || '');
  if (v && v !== 'ALL') sp.set(PFX + key, v); else sp.delete(PFX + key);
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}
function clearAllParams() {
  const sp = new URLSearchParams(window.location.search);
  ['s','vch','make','grp','gdn'].forEach(k => sp.delete(PFX + k));
  window.history.replaceState(null, '', `${window.location.pathname}?${sp.toString()}`);
}

export default function SerialMissing({ db }) {
  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [sortCol, setSortCol] = useState('RawDate');
  const [sortDir, setSortDir] = useState('desc');

  // State init from URL
  const [search,       setSearchRaw]       = useState(() => getP('s'));
  const [filterVch,    setFilterVchRaw]    = useState(() => getP('vch') || 'ALL');
  const [filterMake,   setFilterMakeRaw]   = useState(() => { const v = getP('make'); return v ? v.split('|') : []; });
  const [filterGrp,    setFilterGrpRaw]    = useState(() => { const v = getP('grp');  return v ? v.split('|') : []; });
  const [filterGodown, setFilterGodownRaw] = useState(() => { const v = getP('gdn');  return v ? v.split('|') : []; });

  // Setters that sync state + URL atomically
  const setSearch       = v => { setSearchRaw(v);       setP('s',    v); };
  const setFilterVch    = v => { setFilterVchRaw(v);    setP('vch',  v); };
  const setFilterMake   = v => { setFilterMakeRaw(v);   setP('make', v); };
  const setFilterGrp    = v => { setFilterGrpRaw(v);    setP('grp',  v); };
  const setFilterGodown = v => { setFilterGodownRaw(v); setP('gdn',  v); };

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API}/api/serial-missing?db=${db}`, { headers: { Authorization: `Bearer ${token()}` } });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setData(j.data || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [db]);

  useEffect(() => { if (db) fetchData(); }, [fetchData, db]);

  const allMakes   = useMemo(() => [...new Set(data.map(d => d.Make).filter(Boolean).sort())],    [data]);
  const allGrps    = useMemo(() => [...new Set(data.map(d => d.ItemGroup).filter(Boolean).sort())],[data]);
  const allGodowns = useMemo(() => [...new Set(data.map(d => d.Godown).filter(Boolean).sort())],  [data]);

  const vchCounts = useMemo(() => {
    const c = {}; data.forEach(d => { c[d.VoucherType] = (c[d.VoucherType] || 0) + 1; }); return c;
  }, [data]);

  const stats = useMemo(() => {
    const totalQty  = data.reduce((s, r) => s + (r.Qty || 0), 0);
    return {
      totalRows: data.length, totalQty,
      saleRows:  data.filter(d => d.VoucherType === 'Sale').length,
      purchRows: data.filter(d => d.VoucherType === 'Purchase').length,
    };
  }, [data]);

  const filtered = useMemo(() => {
    let d = data;
    if (filterVch !== 'ALL')    d = d.filter(r => r.VoucherType === filterVch);
    if (filterMake.length)      d = d.filter(r => filterMake.includes(r.Make));
    if (filterGrp.length)       d = d.filter(r => filterGrp.includes(r.ItemGroup));
    if (filterGodown.length)    d = d.filter(r => filterGodown.includes(r.Godown));
    if (search.trim()) {
      const q = search.toLowerCase();
      d = d.filter(r =>
        (r.ItemName||'').toLowerCase().includes(q) ||
        (r.VchNo||'').toLowerCase().includes(q) ||
        (r.Party||'').toLowerCase().includes(q) ||
        (r.Make||'').toLowerCase().includes(q)
      );
    }
    return [...d].sort((a, b) => {
      let av = a[sortCol] ?? '', bv = b[sortCol] ?? '';
      if (sortCol === 'RawDate') return sortDir === 'asc' ? new Date(av) - new Date(bv) : new Date(bv) - new Date(av);
      if (typeof av === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
    });
  }, [data, filterVch, filterMake, filterGrp, filterGodown, search, sortCol, sortDir]);

  const totalQtyFiltered = useMemo(() => filtered.reduce((s, r) => s + (r.Qty || 0), 0), [filtered]);

  function handleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }
  function Th({ col, children }) {
    const active = sortCol === col;
    return (
      <th className={active ? 'sorted' : ''} onClick={() => handleSort(col)}>
        {children}<span className="sort-arrow">{active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
      </th>
    );
  }

  const anyFilter = filterVch !== 'ALL' || filterMake.length || filterGrp.length || filterGodown.length || search;
  function clearAll() {
    setSearchRaw(''); setFilterVchRaw('ALL'); setFilterMakeRaw([]); setFilterGrpRaw([]); setFilterGodownRaw([]);
    clearAllParams();
  }

  const vchTypes = [...new Set(data.map(d => d.VoucherType).filter(Boolean))].sort();

  return (
    <div className="fade-up">
      <div className="page-head">
        <div className="page-title">Serial Missing</div>
        <div className="page-sub">Vouchers where B22 items have no serial/IMEI recorded · {db}</div>
      </div>

      <div className="summary-cards">
        <div className="sum-card">
          <div className="sum-card-label">Total Rows</div>
          <div className="sum-card-value red">{stats.totalRows.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">voucher lines missing serial</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Total Qty</div>
          <div className="sum-card-value orange">{stats.totalQty.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">units without serial</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Sales</div>
          <div className="sum-card-value red">{stats.saleRows.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">sale rows affected</div>
        </div>
        <div className="sum-card">
          <div className="sum-card-label">Purchases</div>
          <div className="sum-card-value accent">{stats.purchRows.toLocaleString('en-IN')}</div>
          <div className="sum-card-sub">purchase rows affected</div>
        </div>
      </div>

      {error && (
        <div className="error-bar">
          <span className="error-icon">⚠</span>
          <div><div>Failed to load data</div><div className="error-detail">{error}</div></div>
        </div>
      )}

      {/* Voucher type chips */}
      <div className="stats-row-scroll">
        <button className={`stat-chip ${filterVch === 'ALL' ? 'active' : ''}`}
          style={{ color:'var(--text2)', borderColor:'var(--border)', background: filterVch==='ALL' ? 'var(--surface3)' : 'transparent' }}
          onClick={() => setFilterVch('ALL')}>
          <span className="stat-dot" style={{ background:'var(--text3)' }} />ALL ({data.length})
        </button>
        {vchTypes.map(vt => {
          const m = VCH_META[vt] || { color:'var(--text2)', bg:'transparent', border:'var(--border)' };
          return (
            <button key={vt} className={`stat-chip ${filterVch === vt ? 'active' : ''}`}
              style={{ color:m.color, background: filterVch===vt ? m.bg : 'transparent', borderColor:m.border }}
              onClick={() => setFilterVch(filterVch === vt ? 'ALL' : vt)}>
              <span className="stat-dot" style={{ background:m.color }} />{vt} ({vchCounts[vt] || 0})
            </button>
          );
        })}
      </div>

      <div className="toolbar">
        <div className="toolbar-row-a">
          <div className="search-box">
            <svg className="search-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
            </svg>
            <input placeholder="Search item, voucher no., party, make…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className={`btn-refresh ${loading ? 'spinning' : ''}`} onClick={fetchData} disabled={loading}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M1 4v6h6M23 20v-6h-6"/><path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10M23 14l-4.64 4.36A9 9 0 0 1 3.51 15"/>
            </svg>
            <span>Refresh</span>
          </button>
        </div>
        <div className="toolbar-row-b">
          <MultiSelect label="Brand/Make" options={allMakes}   selected={filterMake}   onChange={setFilterMake} />
          <MultiSelect label="Item Group" options={allGrps}    selected={filterGrp}    onChange={setFilterGrp} />
          <MultiSelect label="Godown"     options={allGodowns} selected={filterGodown} onChange={setFilterGodown} />
          <button className="btn-reset" onClick={clearAll} disabled={!anyFilter}>↺ Reset</button>
          <span className="count-badge">{filtered.length} rows · {totalQtyFiltered.toLocaleString('en-IN')} qty</span>
        </div>
      </div>

      {loading && !data.length ? (
        <div className="loading-state"><div className="spinner" /><span>Loading serial data…</span></div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <Th col="VoucherType">Type</Th>
                <Th col="RawDate">Date</Th>
                <Th col="VchNo">Vch No</Th>
                <Th col="ItemName">Item Name</Th>
                <Th col="Make">Make</Th>
                <Th col="ItemGroup">Group</Th>
                <Th col="Qty">Qty</Th>
                <Th col="Godown">Godown</Th>
                <Th col="Party">Party</Th>
                <Th col="Series">Series</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={10}>
                  <div className="empty-state">
                    <div className="empty-icon">✅</div>
                    <h3>No serial issues found</h3>
                    <p>All B22 items have serials recorded</p>
                  </div>
                </td></tr>
              ) : filtered.map((r, i) => {
                const m = VCH_META[r.VoucherType] || { color:'var(--text2)', bg:'transparent', border:'var(--border)' };
                return (
                  <tr key={i}>
                    <td><span className="badge" style={{ color:m.color, background:m.bg, borderColor:m.border }}>{r.VoucherType}</span></td>
                    <td className="mono dim" style={{ fontSize:11 }}>{r.VchDate}</td>
                    <td className="mono">{r.VchNo}</td>
                    <td className="wrap">{r.ItemName}</td>
                    <td className="dim">{r.Make || <span className="muted">—</span>}</td>
                    <td className="dim">{r.ItemGroup || <span className="muted">—</span>}</td>
                    <td className="num" style={{ color:'var(--amber)', fontWeight:600 }}>{r.Qty}</td>
                    <td className="dim">{r.Godown || <span className="muted">—</span>}</td>
                    <td style={{ maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{r.Party || <span className="muted">—</span>}</td>
                    <td className="dim">{r.Series || <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
